import {
  BarChart3,
  Building2,
  ClipboardList,
  FileStack,
  Home,
  Layers,
  LogOut,
  MessageSquareText,
  Sparkles,
  Upload,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/features/auth/store";
import LanguageToggle from "@/i18n/LanguageToggle";
import { cn } from "@/lib/cn";

interface NavItem {
  to: string;
  /**
   * 번역 키 (네임스페이스 ``common`` 의 ``layout.nav.*``).
   * 라벨 자체는 ``useTranslation`` 의 ``t()`` 로 렌더 시점에 해석.
   */
  i18nKey: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", i18nKey: "layout.nav.dashboard", icon: Home },
  { to: "/upload", i18nKey: "layout.nav.upload", icon: Upload },
  { to: "/requests", i18nKey: "layout.nav.requests", icon: Layers },
  { to: "/jobs", i18nKey: "layout.nav.jobs", icon: FileStack },
  { to: "/guide", i18nKey: "layout.nav.guide", icon: ClipboardList },
  {
    to: "/admin/work-stats",
    i18nKey: "layout.nav.adminWorkStats",
    icon: BarChart3,
    adminOnly: true,
  },
  { to: "/admin/users", i18nKey: "layout.nav.adminUsers", icon: Users, adminOnly: true },
  { to: "/admin/agents", i18nKey: "layout.nav.adminAgents", icon: Building2, adminOnly: true },
  {
    to: "/admin/prompts",
    i18nKey: "layout.nav.adminPrompts",
    icon: MessageSquareText,
    adminOnly: true,
  },
  {
    to: "/admin/suggestions",
    i18nKey: "layout.nav.adminSuggestions",
    icon: Sparkles,
    adminOnly: true,
  },
];

export default function Layout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);

  function handleLogout(): void {
    clear();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden w-60 flex-shrink-0 border-r border-slate-200 bg-white md:block">
        <div className="px-6 py-5">
          <h1 className="text-base font-bold tracking-tight text-slate-900">
            {t("layout.appName")}
          </h1>
          <p className="mt-0.5 text-[11px] text-slate-400">{t("layout.appTagline")}</p>
        </div>
        <nav className="mt-2 space-y-0.5 px-3" aria-label={t("layout.nav.ariaLabel")}>
          {NAV_ITEMS.filter((item) => !item.adminOnly || user?.role === "admin").map(
            ({ to, i18nKey, icon: Icon }) => {
              const active =
                to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition",
                    active
                      ? "bg-blue-50 font-medium text-blue-700"
                      : "text-slate-600 hover:bg-slate-100",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {t(i18nKey)}
                </Link>
              );
            },
          )}
        </nav>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-6">
          <div className="md:hidden">
            <span className="text-sm font-semibold text-slate-900">{t("layout.appName")}</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <LanguageToggle />
            {user && (
              <div className="text-right">
                <p className="text-xs font-medium text-slate-700">{user.email}</p>
                <p className="text-[10px] uppercase tracking-wide text-slate-400">{user.role}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 transition hover:bg-slate-50"
              aria-label={t("layout.logout")}
            >
              <LogOut className="h-3.5 w-3.5" />
              {t("layout.logout")}
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
